import { Component, OnInit, Output, EventEmitter, Input } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute } from '@angular/router';
import { CoreService } from '../../../../services/core.service';
import { SubmitGridComponent } from '../../components/submit-grid/submit-grid.component';
import { SectionGridComponent } from '../../components/section-grid/section-grid.component';
import { StepperGridComponent } from '../../components/stepper-grid/stepper-grid.component';
import { FooterGridComponent } from '../../components/footer-grid/footer-grid.component';

@Component({
  selector: 'app-create-site',
  standalone: true,
  imports: [ReactiveFormsModule, CommonModule, SectionGridComponent, SubmitGridComponent, StepperGridComponent, FooterGridComponent],
  templateUrl: './create-site.component.html',
  styleUrl: './create-site.component.scss'
})

export class CreateSiteComponent {

  @Input() embeddedMode = false;
  @Input() companyIdInput: string | null = null;
  siteForm: FormGroup;
  companyId: string | null = null;
  formData: any = { ID: null };

  // File handling properties
  fullLogoFile: File | null = null;
  thumbFile: File | null = null;
  fullLogoPreview: string | null = null;
  thumbPreview: string | null = null;
  fullLogoError: string = '';
  thumbError: string = '';
  @Input() hasBorder: boolean = true;

  @Output() onSubmitClbk = new EventEmitter<any>();

  constructor(
    private fb: FormBuilder,
    private coreService: CoreService,
    private router: Router,
    private route: ActivatedRoute
  ) {
    this.siteForm = this.fb.group({
      Domain: [''],
      siteUrl: [''],
      cmsType: [''],
      companyID: [{ value: '', disabled: true }, Validators.required]
    });
  }

  ngOnInit() {
    // Get company ID from route parameter
    this.route.params.subscribe(params => {
      this.companyId = this.companyIdInput || params['company'];
      console.log('Company ID from route:', this.companyId);

      if (this.companyId) {
        this.siteForm.patchValue({
          companyID: this.companyId
        });
      }
    });

    // Check if we're editing an existing site
    // if (this.formData.ID) {
    //   this.loadSiteData(this.formData.ID);
    // }
  }

  // File change handler
  onFileChange(event: Event, type: 'full' | 'thumb'): void {
    const input = event.target as HTMLInputElement;

    if (!input.files || input.files.length === 0) {
      return;
    }

    const file = input.files[0];

    // Validate file type
    if (file.type !== 'image/png') {
      if (type === 'full') {
        this.fullLogoError = 'Only PNG files are allowed';
      } else {
        this.thumbError = 'Only PNG files are allowed';
      }
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      if (type === 'full') {
        this.fullLogoError = 'File size must be less than 5MB';
      } else {
        this.thumbError = 'File size must be less than 5MB';
      }
      return;
    }

    // Clear errors
    if (type === 'full') {
      this.fullLogoError = '';
      this.fullLogoFile = file;
      this.generatePreview(file, 'full');
    } else {
      this.thumbError = '';
      this.thumbFile = file;
      this.generatePreview(file, 'thumb');
    }
  }

  // Generate image preview
  private generatePreview(file: File, type: 'full' | 'thumb'): void {
    const reader = new FileReader();

    reader.onload = (e: ProgressEvent<FileReader>) => {
      const result = e.target?.result as string;

      if (type === 'full') {
        this.fullLogoPreview = result;
      } else {
        this.thumbPreview = result;
      }
    };

    reader.readAsDataURL(file);
  }

  // Helper method to get form controls
  getControl(controlName: string) {
    return this.siteForm.get(controlName);
  }

  onSubmit(): void {
    // Validate required files
    if (!this.fullLogoFile) {
      this.fullLogoError = 'Primary logo is required';
      return;
    }

    if (!this.thumbFile) {
      this.thumbError = 'Thumbnail image is required';
      return;
    }

    if (this.siteForm.valid) {
      // Create FormData for file upload
      const formData = new FormData();

      // Get form values including disabled controls
      const rawData = this.siteForm.getRawValue();

      // Add form fields to FormData
      formData.append('Domain', rawData.Domain || '');
      formData.append('siteUrl', rawData.siteUrl || '');
      formData.append('cmsType', rawData.cmsType || '');
      formData.append('companyID', rawData.companyID);

      // Add files
      if (this.fullLogoFile) {
        formData.append('logo_file_path', this.fullLogoFile, this.fullLogoFile.name);
      }

      if (this.thumbFile) {
        formData.append('thumbnail_file_path', this.thumbFile, this.thumbFile.name);
      }

      // Determine if this is create or update
      const endpoint = this.formData.ID
      // ? `manageAccount/site/update/${this.formData.ID}`
      // : 'manageAccount/site/store';

      this.coreService.postData('manageAccount/site/store', formData).subscribe({
        next: (data) => {
          if (this.embeddedMode) {
            this.onSubmitClbk.emit(data);
          } else {
            console.log('Site operation successful:', data);

            const companyId = data.companyID || data.companyID || data.data?.companyID || data.data?.companyID;
            const siteId = data.ID || data.ID || data.data?.ID || data.data?.ID;

            if (companyId) {
              this.router.navigate(['/manage-account/create-platform', this.companyId, siteId]);
            } else {
              console.error('No company ID found in response:', data);
            }

            // Optional: Reset form
            // this.resetForm();
          }
        },
        error: (error) => {
          console.error('Error creating/updating site:', error);
          // Handle specific errors
          if (error.status === 413) {
            alert('File too large. Please upload files smaller than 5MB.');
          }
        }
      });
    } else {
      this.markFormGroupTouched(this.siteForm);
    }
  }

  clearFile(type: 'full' | 'thumb'): void {
    if (type === 'full') {
      this.fullLogoFile = null;
      this.fullLogoPreview = null;
      this.fullLogoError = '';
    } else {
      this.thumbFile = null;
      this.thumbPreview = null;
      this.thumbError = '';
    }
  }

  private markFormGroupTouched(formGroup: FormGroup) {
    Object.values(formGroup.controls).forEach(control => {
      control.markAsTouched();

      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }

  resetForm(): void {
    this.siteForm.reset({
      Domain: '',
      siteUrl: '',
      cmsType: '',
      companyID: this.companyId || ''
    });

    // Clear files
    this.clearFile('full');
    this.clearFile('thumb');

    this.siteForm.markAsPristine();
    this.siteForm.markAsUntouched();

    // Keep company field disabled if it exists
    if (this.companyId) {
      this.siteForm.get('companyID')?.disable();
    }
  }

}
